import './PredictionImages.scss';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from '../../../../__legacy__/components/Loader';
import { scrollTop } from '../../../../__legacy__/helpers/react.helpers';
import { Home } from '../../../../__legacy__/routes/config/Home';
import { S_Alert } from '../../../../design/alert/S_Alert';
import { S_TabsCard } from '../../../../design/card/tabsCard/S_TabsCards';
import { S_Pagination, S_PaginationProps } from '../../../../design/pagination/S_Pagination';
import { Thumbnails } from '../../../../design/thumbnails/Thumbnails';
import { IPredictionImage } from '../../jobPredictions.model';
import { IValidJobType, JOB_STATUS, JOB_TYPE, TRAINING_VARIANT } from '../../jobs.model';
import { IJobThreshold } from '../../jobThreshold.store';
import { useDebouncedEffect } from '../../utils';
import { RecalculateMetricsSlider, SliderWithLabel } from '../SliderWithLabel';
import { ToggleSwitchWithLabel } from '../ToggleSwitchWithLabel';
import { PredictionImageThumbnail } from './PredictionImageThumbnail';

interface IPredictionImagesProps {
  allowImageDetails: boolean;
  jobType: IValidJobType;
  detailsImages: ReadonlyArray<IPredictionImage>;
  pagination: S_PaginationProps;
  authToken: string;
  showSwitchGroup: boolean;
  disableSwitch: boolean;
  showPredictions: boolean;
  onSwitchChange: () => Promise<void>;
  isLoading: boolean;
  modelVariant: TRAINING_VARIANT | null;
  thresholdConfig: IJobThreshold | null;
  setThresholdRange: (min: number, max: number) => void;
  setOptimalThreshold: (value: number) => void;
  updateThresholdAsync?: (min: number, max: number) => Promise<void>;
  status?: JOB_STATUS;
  isInferenceOnDataset: boolean;
}

export const PredictionImages = observer(
  ({
    detailsImages,
    pagination,
    authToken,
    showSwitchGroup,
    disableSwitch,
    showPredictions,
    onSwitchChange,
    isLoading,
    jobType,
    allowImageDetails,
    modelVariant,
    thresholdConfig,
    setThresholdRange,
    setOptimalThreshold,
    updateThresholdAsync,
    status,
    isInferenceOnDataset,
  }: IPredictionImagesProps): ReactElement => {
    const params = useParams<{ workspaceId: string; jobId: string }>();
    const { t } = useTranslator();

    const [inputsValues, setInputsValues] = useState<{ min: string; max: string }>({ min: '', max: '' });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (!modelVariant || !thresholdConfig) {
        return;
      }

      if (
        [TRAINING_VARIANT.ANOMALY_DETECTION_7, TRAINING_VARIANT.ANOMALY_DETECTION_8, TRAINING_VARIANT.ANOMALY_DETECTION_4].includes(modelVariant) &&
        thresholdConfig.minThreshold !== null &&
        thresholdConfig.maxThreshold !== null
      ) {
        setInputsValues({ min: thresholdConfig.minThreshold.toFixed(1), max: thresholdConfig.maxThreshold.toFixed(1) });
        return;
      }
    }, [thresholdConfig, modelVariant]);

    useDebouncedEffect(
      () => {
        const min = parseFloat(inputsValues.min);
        const max = parseFloat(inputsValues.max);

        if (isNaN(min) || isNaN(max) || !thresholdConfig) {
          return;
        }

        setThresholdRange(min, max);
      },
      [inputsValues],
      500
    );

    const handleChangeInput =
      (title: string) =>
      (value: string): void => {
        setInputsValues((state) => ({ ...state, [title]: value }));
      };

    const handleDoubleThresholdChange = (values: readonly number[]): void => {
      setInputsValues(() => ({ min: values[0].toFixed(1), max: values[1].toFixed(1) }));
    };

    const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
      scrollTop();
      await pagination.onChange(pageNumber, pageSize);
    };

    const handleThresholdChange = (value: number): void => {
      if (isNaN(value)) {
        return;
      }

      setOptimalThreshold(value);
    };

    const saveThresholdsHandler = async (): Promise<void> => {
      if (!updateThresholdAsync || isSaving || !thresholdConfig || thresholdConfig.maxThreshold === null || thresholdConfig.minThreshold === null) {
        return;
      }

      setIsSaving(true);
      await updateThresholdAsync(thresholdConfig.minThreshold, thresholdConfig.maxThreshold);
      setIsSaving(false);
    };

    const getOnClickLink = (image: IPredictionImage): string => {
      const parameters = {
        workspaceId: params.workspaceId,
        jobId: params.jobId,
        imageId: image.id,
      };
      switch (jobType) {
        case JOB_TYPE.INFERENCE:
          return Home.Inferences.Details.PredictionInspector.withParams(parameters);
        case JOB_TYPE.TRAINING:
          return Home.Trainings.Details.PredictionInspector.withParams(parameters);
        default:
          return '';
      }
    };

    const getThresholdForImage = (): number | number[] => {
      if (!modelVariant || !thresholdConfig) {
        return 0;
      }

      if (
        [TRAINING_VARIANT.ANOMALY_DETECTION_7, TRAINING_VARIANT.ANOMALY_DETECTION_8, TRAINING_VARIANT.ANOMALY_DETECTION_4].includes(modelVariant) &&
        thresholdConfig.minThreshold !== null &&
        thresholdConfig.maxThreshold !== null
      ) {
        return [thresholdConfig.minThreshold, thresholdConfig.maxThreshold];
      } else {
        return thresholdConfig.optimalThreshold;
      }
    };

    const isSliderDisabled = !showPredictions || isLoading;

    return (
      <S_TabsCard className='prediction-images'>
        <S_Alert color='info'>
          <div>{t(isInferenceOnDataset ? 'images_details_from_newest_dataset' : 'images_details_from_newest_project')}</div>
        </S_Alert>
        {showSwitchGroup && (
          <div className='prediction-controls'>
            <ToggleSwitchWithLabel
              label={t('show_predictions')}
              checked={showPredictions}
              onChange={() => {
                void onSwitchChange();
              }}
              disabled={disableSwitch}
            />
            {modelVariant &&
              thresholdConfig &&
              ![TRAINING_VARIANT.ANOMALY_DETECTION_7, TRAINING_VARIANT.ANOMALY_DETECTION_8, TRAINING_VARIANT.ANOMALY_DETECTION_4].includes(modelVariant) && (
                <SliderWithLabel
                  label={t('threshold.title')}
                  min={thresholdConfig.minScore}
                  max={thresholdConfig.maxScore}
                  step={thresholdConfig.thresholdStep}
                  value={thresholdConfig.optimalThreshold}
                  onChange={handleThresholdChange}
                  disabled={isSliderDisabled}
                />
              )}
            {thresholdConfig && typeof thresholdConfig.minThreshold === 'number' && typeof thresholdConfig.maxThreshold === 'number' && (
              <RecalculateMetricsSlider
                label={t('threshold.title')}
                min={thresholdConfig.minScore}
                max={thresholdConfig.maxScore}
                step={thresholdConfig.thresholdStep}
                value={[thresholdConfig.minThreshold, thresholdConfig.maxThreshold]}
                inputsValues={inputsValues}
                onInputChanged={handleChangeInput}
                onChange={handleDoubleThresholdChange}
                disabled={isSliderDisabled}
                saveThresholdsHandler={updateThresholdAsync ? saveThresholdsHandler : undefined}
                isSaving={isSaving || status === JOB_STATUS.SAVING}
              />
            )}
          </div>
        )}
        <div className='project-details-images'>
          <Loader isLoading={isLoading} />
          {detailsImages.length === 0 && <div className='no-image-message'>{t('no_images')}</div>}
          {detailsImages.length > 0 && (
            <div className='thumbnails-container'>
              <Thumbnails isLoading={isLoading}>
                {!isLoading &&
                  detailsImages.map((image: IPredictionImage) => (
                    <PredictionImageThumbnail
                      onClickLink={allowImageDetails ? getOnClickLink(image) : undefined}
                      key={image.id}
                      image={image}
                      authToken={authToken}
                      threshold={getThresholdForImage()}
                      showPredictions={showPredictions}
                      variant={modelVariant}
                    />
                  ))}
              </Thumbnails>
              <div className='thumbnails-paging'>
                <S_Pagination
                  {...pagination}
                  pageSizeLabel='pagination.showImages'
                  onChange={handlePaginationChange}
                />
              </div>
            </div>
          )}
        </div>
      </S_TabsCard>
    );
  }
);
